import { ArrayTool } from './array-tool';

export interface Func<T, V> {
  (v: T, i?: number): V;
}

export interface Action<T> extends Func<T, void> {
}

export interface Predicate<T> extends Func<T, boolean> {
}

export interface LinqEnumerable<T> {
  any(): boolean;
  values(): T[];
  forEach(p: Action<T>);
  forEachReversed(p: Action<T>);
  where(p: Predicate<T>): LinqArray<T>;
  whereNot(p: Predicate<T>): LinqArray<T>;
  select<V>(p: Func<T, V>): LinqArray<V>;
  selectMany<V>(p: Func<T, LinqIterable<V>>): LinqArray<V>;
  selectIndexWhere(p: Predicate<T>): LinqArray<number>;
  distinct(): LinqEnumerable<T>;
  except(values: LinqIterable<T>): LinqArray<T>;
  contains(v: T): boolean;
  toArray(): LinqArray<T>;
  sortBy<V extends string | number>(selector: Func<T, V>): LinqArray<T>;
  groupBy<V extends string | number>(selector: Func<T, V>): LinqArray<LinqGroup<T, V>>;
  cast<V>(): LinqEnumerable<V>;
}

export interface LinqCollection<T> extends LinqEnumerable<T> {
  count(): number;
  add(v: T);
  removeAll(p: Predicate<T>);
  single(): T;
}

export interface LinqArray<T> extends LinqCollection<T> {
  reverse(): LinqArray<T>;
  toSet(): LinqSet<T>;
  first(): T;
  last(): T;
}

export interface LinqGroup<T, V> extends LinqArray<T> {
  key: V;
}

export interface LinqSet<T> extends LinqCollection<T> {
  remove(v: T);
  toArray(): LinqArray<T>;
}

export type LinqIterable<T> = LinqEnumerable<T> | T[];

export class linq {
  static range(start: number, count: number): LinqArray<number> {
    const range = Array.from(Array(Math.max(0, count || 0)).keys());
    return linq.array(start == 0 ? range : range.map(i => i + (start || 0)));
  }

  static array<T>(values: LinqIterable<T>): LinqArray<T> {
    const _values = linq.valuesOf(values) || [];
    return new LinqArrayImpl(_values);
  }

  static set<T>(values: LinqIterable<T> = undefined): LinqSet<T> {
    const _values = linq.valuesOf(values) || [];
    const _set = new Set(_values);
    return new LinqSetImpl(_set);
  }

  static enumerableFor<T>(_values: LinqIterable<T>): LinqEnumerable<T> {
    return !_values || Array.isArray(_values)
      ? linq.array(_values as T[])
      : _values as LinqEnumerable<T>;
  }

  static setOf<T>(_values: LinqIterable<T>): LinqSet<T> {
    return (_values as LinqSetImpl<T>)._set ?
      _values as LinqSet<T> :
      linq.set(_values);
  }

  private static valuesOf<T>(_values: LinqIterable<T>): T[] {
    return !_values || Array.isArray(_values)
      ? _values as T[]
      : (_values as LinqEnumerable<T>).values();
  }
}

abstract class CollectionBaseImpl<T> implements LinqCollection<T>{
  abstract any(): boolean;
  abstract contains(v: T): boolean;
  abstract count(): number;
  abstract add(v: T);
  abstract removeAll(p: Predicate<T>);
  abstract distinct(): LinqEnumerable<T>;
  abstract toArray(): LinqArray<T>;

  constructor(public values: () => T[]) { }

  forEach(p: Action<T>) { this.values().forEach((v, i) => p(v, i)); }
  forEachReversed(p: Action<T>) { this.values().reverse().forEach((v, i) => p(v, i)); }
  where(p: Predicate<T>): LinqArray<T> { return linq.array(this.values().filter((v, i) => p(v, i))); }
  whereNot(p: Predicate<T>): LinqArray<T> { return linq.array(this.values().filter((v, i) => !p(v, i))); }
  select<V>(p: Func<T, V>): LinqArray<V> { return linq.array(this.values().map((v, i) => p(v, i))); }
  cast<V>(): LinqEnumerable<V> { return this.select(v => <any>v as V); }
  single(): T { return this.count() == 1 ? this.values()[0] : undefined; }

  selectMany<V>(p: Func<T, LinqIterable<V>>): LinqArray<V> {
    var values = this.values()
      .map((v, i) => p(v, i))
      .map(linq.enumerableFor)
      .map(a => a.values())
      .reduce(ArrayTool.concat, []);
    return linq.array(values);
  }

  selectIndexWhere(p: Predicate<T>): LinqArray<number> {
    return linq.array(this.values()
      .map((value, index) => ({ value, index }))
      .filter(t => p(t.value, t.index))
      .map(t => t.index));
  }

  except(values: LinqIterable<T>): LinqArray<T> {
    const e = linq.setOf(values);
    return linq.array(this.values().filter(v => !e.contains(v)));
  }

  sortBy<V extends string | number>(selector: Func<T, V>): LinqArray<T> {
    return linq.array(this
      .select(value => ({ value: value, sortValue: selector(value) }))
      .values()
      .sort((a, b) => a.sortValue === b.sortValue ? 0 : a.sortValue > b.sortValue ? 1 : -1)
      .map(v => v.value));
  }

  groupBy<V extends string | number>(selector: Func<T, V>): LinqArray<LinqGroup<T, V>> {
    const values = this.select(value => ({ key: selector(value), value }));
    return values
      .select(v => v.key)
      .distinct()
      .select(key => new LinqGroupImpl(key, values.where(v => v.key === key).select(v => v.value).values()))
      .toArray();
  }
}

class LinqArrayImpl<T> extends CollectionBaseImpl<T> implements LinqArray<T> {
  constructor(public _values: T[]) { super(() => _values); }

  any(): boolean { return this._values.length > 0; }
  contains(v: T): boolean { return this._values.indexOf(v) >= 0; }
  count(): number { return this._values.length; }
  add(v: T) { return this._values.push; }
  removeAll(p: Predicate<T>) { return linq.array(ArrayTool.removeAll(this._values, p)); }
  reverse(): LinqArray<T> { return linq.array(this._values.reverse()); }
  toSet(): LinqSet<T> { return linq.set(this._values); }
  distinct(): LinqEnumerable<T> { return linq.set(this); }
  toArray(): LinqArray<T> { return this; }
  first(): T { return this._values[0]; }
  last(): T { return this._values[this._values.length - 1]; }
}

class LinqSetImpl<T> extends CollectionBaseImpl<T> implements LinqSet<T> {
  constructor(public _set: Set<T>) { super(() => Array.from(_set.values())); }

  count(): number { return this._set.size; }
  add(v: T) { this._set.add(v); }
  removeAll(p: Predicate<T>) { this.values().filter(p).forEach(this._set.delete); }
  any(): boolean { return this._set.size > 0; }
  contains(v: T): boolean { return this._set.has(v); }
  remove(v: T) { this._set.delete(v); }
  distinct(): LinqEnumerable<T> { return this; }
  toArray(): LinqArray<T> { return linq.array(this.values()); }
}

class LinqGroupImpl<T, V> extends LinqArrayImpl<T> implements LinqGroup<T, V> {
  constructor(public key: V, values: T[]) { super(values); }
}





// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular2-router-loader/src!./TypeScript/core/tools/linq.ts